import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {Result} from "@vesta/error-handling-remix/result";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

export const DULUX_ACTIONS_API_BASE_PATH = "/api/workspace-specific/dulux/actions";
export const DULUX_BLUE_STORE_ACTIONS_API_BASE_PATH = "/api/workspace-specific/dulux/blue-store/actions";

export const duluxActionResponseSchema = zod.object({
    appointmentEventId: uuidZodValidator.optional(),
    archivedEventIds: zod.array(uuidZodValidator).optional(),
    callEventId: uuidZodValidator.optional(),
    dispositionId: uuidZodValidator.optional(),
    eventId: uuidZodValidator.optional(),
    followUpAppointmentEventId: uuidZodValidator.optional(),
    followUpCallEventId: uuidZodValidator.optional(),
    eventContainerId: uuidZodValidator.optional(),
    loggedEventId: uuidZodValidator.optional(),
    onboardingEventId: uuidZodValidator.optional(),
    restoredEventIds: zod.array(uuidZodValidator).optional(),
    suggestedLeadStatusId: uuidZodValidator.optional(),
    updatedEventIds: zod.array(uuidZodValidator).optional(),
    updatedEventContainerId: uuidZodValidator.optional(),
});
const duluxEventContainerNoteAttachmentResponseSchema = zod.object({
    assetId: uuidZodValidator,
    fileName: zod.string(),
    mimeType: zod.string().nullable().optional(),
    createdAt: zod.string(),
});
const duluxEventContainerNoteResponseSchema = zod.object({
    id: uuidZodValidator,
    eventContainerId: uuidZodValidator,
    bodyMarkdown: zod.string(),
    bodyPlainTextLength: zod.number(),
    createdBy: uuidZodValidator,
    createdAt: zod.string(),
    updatedAt: zod.string(),
    updatedBy: uuidZodValidator,
    attachments: zod.array(duluxEventContainerNoteAttachmentResponseSchema),
    scopeType: zod.enum(["event", "eventContainer"]),
    eventId: uuidZodValidator.nullable(),
    eventTemplateId: uuidZodValidator.nullable(),
});
const duluxEventContainerNoteMutationResponseSchema = zod.object({
    eventContainerId: uuidZodValidator,
    note: duluxEventContainerNoteResponseSchema,
});
const duluxEventContainerNoteDeleteResponseSchema = zod.object({
    eventContainerId: uuidZodValidator,
    noteId: uuidZodValidator,
});

export type DuluxActionResponse = zod.infer<typeof duluxActionResponseSchema>;
export type DuluxEventContainerNoteResponse = zod.infer<typeof duluxEventContainerNoteResponseSchema>;
export type DuluxEventContainerNoteMutationResponse = zod.infer<typeof duluxEventContainerNoteMutationResponseSchema>;
export type DuluxEventContainerNoteDeleteResponse = zod.infer<typeof duluxEventContainerNoteDeleteResponseSchema>;

export type DuluxScheduleCallInlineRequest = {
    eventContainerId: Uuid;
    scheduledOnIso: Iso8601DateTime;
    workspaceId: Uuid;
};

export type DuluxScheduleCallRequest = {
    eventContainerId: Uuid;
    phoneNumber?: string | null;
    scheduledOnIso: Iso8601DateTime;
    workspaceId: Uuid;
};

export type DuluxScheduleAppointmentRequest = {
    eventContainerId: Uuid;
    scheduledOnIso: Iso8601DateTime;
    workspaceId: Uuid;
};

export type DuluxLogCallRequest = {
    dispositionId: Uuid;
    eventId?: Uuid | null;
    eventContainerId: Uuid;
    loggedOnIso: Iso8601DateTime;
    phoneNumber?: string | null;
    remarks?: string | null;
    scheduledAppointmentOnIso?: Iso8601DateTime | null;
    scheduledCallOnIso?: Iso8601DateTime | null;
    workspaceId: Uuid;
};

export type DuluxLogAppointmentRequest = {
    dispositionId: Uuid;
    eventId?: Uuid | null;
    eventContainerId: Uuid;
    loggedOnIso: Iso8601DateTime;
    phoneNumber?: string | null;
    remarks?: string | null;
    scheduledAppointmentOnIso?: Iso8601DateTime | null;
    scheduledCallOnIso?: Iso8601DateTime | null;
    workspaceId: Uuid;
};

export type DuluxEditScheduledCallRequest = {
    eventId: Uuid;
    scheduledOnIso: Iso8601DateTime;
    workspaceId: Uuid;
};

export type DuluxEditScheduledAppointmentRequest = {
    eventId: Uuid;
    scheduledOnIso: Iso8601DateTime;
    workspaceId: Uuid;
};

export type DuluxCancelCallRequest = {
    cancellationRemarks: string;
    eventId: Uuid;
    workspaceId: Uuid;
};

export type DuluxCancelAppointmentRequest = {
    cancellationRemarks: string;
    eventId: Uuid;
    workspaceId: Uuid;
};

export type DuluxDisposeRequest = {
    dispositionId: Uuid;
    eventContainerId: Uuid;
    remarks?: string | null;
    scheduledAppointmentOnIso?: Iso8601DateTime | null;
    scheduledCallOnIso?: Iso8601DateTime | null;
    workspaceId: Uuid;
};

export type DuluxEventEditPanelSaveRequest = {
    data?: Record<string, unknown>;
    eventAdditionalColumnValues?: Record<string, unknown>;
    eventId: Uuid;
    eventPatch?: Record<string, unknown>;
    eventContainerAdditionalColumnValues?: Record<string, unknown>;
    eventContainerPatch?: {
        additionalColumnValues?: Record<string, unknown>;
        data?: Record<string, unknown>;
        eventContainerAdditionalColumnValues?: Record<string, unknown>;
        organizationalUnitId?: Uuid | null;
    };
    organizationalUnitId?: Uuid | null;
    templateId: Uuid;
    workspaceId: Uuid;
};

export type DuluxUpdateEventContainerProfileRequest = {
    additionalColumnValues?: Record<string, unknown>;
    data?: Record<string, unknown>;
    eventUpdates?: Array<{
        additionalColumnValues?: Record<string, unknown>;
        data?: Record<string, unknown>;
        eventAdditionalColumnValues?: Record<string, unknown>;
        eventId?: Uuid | null;
        templateId: Uuid;
    }>;
    eventContainerAdditionalColumnValues?: Record<string, unknown>;
    eventContainerId: Uuid;
    organizationalUnitId?: Uuid | null;
    workspaceId: Uuid;
};

export type DuluxEventTemplateRequest = {
    eventId: Uuid;
    templateId: Uuid;
    workspaceId: Uuid;
};

export type DuluxEventContainerIdRequest = {
    eventContainerId: Uuid;
    workspaceId: Uuid;
};

export type DuluxEventContainerNoteAttachment = {
    assetId: Uuid;
    fileName: string;
    mimeType?: string | null;
    createdAt: Iso8601DateTime;
};

export type DuluxCreateEventContainerNoteRequest = {
    eventContainerId: Uuid;
    note: {
        attachments: Array<DuluxEventContainerNoteAttachment>;
        bodyMarkdown: string;
        scopeType?: "eventContainer";
    };
    workspaceId: Uuid;
};

export type DuluxUpdateEventContainerNoteRequest = {
    eventContainerId: Uuid;
    note: {
        attachments: Array<DuluxEventContainerNoteAttachment>;
        bodyMarkdown: string;
        scopeType?: "eventContainer";
    };
    noteId: Uuid;
    workspaceId: Uuid;
};

export type DuluxDeleteEventContainerNoteRequest = {
    eventContainerId: Uuid;
    noteId: Uuid;
    workspaceId: Uuid;
};

function buildDuluxActionHeaders(args: {
    accessToken: string;
    workspaceId: Uuid;
}): Record<string, string> {
    return {
        Authorization: `Bearer ${args.accessToken}`,
        "Content-Type": "application/json",
        "X-Workspace-Id": args.workspaceId,
    };
}

async function postDuluxAction(args: {
    accessToken: string;
    body: Record<string, unknown>;
    path: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<DuluxActionResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_ACTIONS_API_BASE_PATH}${args.path}`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.workspaceId,
            }),
            body: JSON.stringify(args.body),
        },
        duluxActionResponseSchema,
    );
}

export function duluxScheduleCallAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxScheduleCallRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/schedule-call",
        body: args.request,
    });
}

export function duluxScheduleCallInlineAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxScheduleCallInlineRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/schedule-call-inline",
        body: args.request,
    });
}

export function duluxScheduleAppointmentAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxScheduleAppointmentRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/schedule-appointment",
        body: args.request,
    });
}

export function duluxLogCallAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxLogCallRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/log-call",
        body: args.request,
    });
}

export function duluxLogAppointmentAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxLogAppointmentRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/log-appointment",
        body: args.request,
    });
}

export function duluxEditScheduledCallAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxEditScheduledCallRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/edit-scheduled-call",
        body: args.request,
    });
}

export function duluxEditScheduledAppointmentAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxEditScheduledAppointmentRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/edit-scheduled-appointment",
        body: args.request,
    });
}

export function duluxCancelCallAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxCancelCallRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/cancel-call",
        body: args.request,
    });
}

export function duluxCancelAppointmentAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxCancelAppointmentRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/cancel-appointment",
        body: args.request,
    });
}

export function duluxDisposeAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxDisposeRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/dispose",
        body: args.request,
    });
}

export function duluxEventEditPanelSaveAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxEventEditPanelSaveRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/event-edit-panel-save",
        body: args.request,
    });
}

export function duluxUpdateEventContainerProfileAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxUpdateEventContainerProfileRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/update-event-container-profile",
        body: args.request,
    });
}

export function duluxArchiveEventAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxEventTemplateRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/archive-event",
        body: args.request,
    });
}

export function duluxRestoreEventAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxEventTemplateRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/restore-event",
        body: args.request,
    });
}

export function duluxArchiveEventContainerAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxEventContainerIdRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/archive-event-container",
        body: args.request,
    });
}

export function duluxRestoreEventContainerAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxEventContainerIdRequest;
}): Promise<Result<DuluxActionResponse>> {
    return postDuluxAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/restore-event-container",
        body: args.request,
    });
}

export function duluxCreateEventContainerNoteAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxCreateEventContainerNoteRequest;
}): Promise<Result<DuluxEventContainerNoteMutationResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_ACTIONS_API_BASE_PATH}/event-container-notes/create`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        duluxEventContainerNoteMutationResponseSchema,
    );
}

export function duluxBlueStoreCreateEventContainerNoteAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxCreateEventContainerNoteRequest;
}): Promise<Result<DuluxEventContainerNoteMutationResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_BLUE_STORE_ACTIONS_API_BASE_PATH}/event-container-notes/create`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        duluxEventContainerNoteMutationResponseSchema,
    );
}

export function duluxUpdateEventContainerNoteAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxUpdateEventContainerNoteRequest;
}): Promise<Result<DuluxEventContainerNoteMutationResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_ACTIONS_API_BASE_PATH}/event-container-notes/update`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        duluxEventContainerNoteMutationResponseSchema,
    );
}

export function duluxBlueStoreUpdateEventContainerNoteAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxUpdateEventContainerNoteRequest;
}): Promise<Result<DuluxEventContainerNoteMutationResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_BLUE_STORE_ACTIONS_API_BASE_PATH}/event-container-notes/update`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        duluxEventContainerNoteMutationResponseSchema,
    );
}

const duluxBlueStoreActionResponseSchema = zod.object({
    eventContainerId: uuidZodValidator,
    eventId: uuidZodValidator,
});
const duluxBlueStoreEventContainerProfileUpdateResponseSchema = zod.object({
    eventContainerId: uuidZodValidator,
    updatedEventIds: zod.array(uuidZodValidator),
});

export type DuluxBlueStoreActionResponse = zod.infer<typeof duluxBlueStoreActionResponseSchema>;
export type DuluxBlueStoreEventContainerProfileUpdateResponse = zod.infer<
    typeof duluxBlueStoreEventContainerProfileUpdateResponseSchema
>;

export type DuluxBlueStoreUpdateEventContainerProfileRequest = {
    eventContainerAdditionalColumnValues: Record<string, unknown>;
    eventContainerId: Uuid;
    eventUpdates: Array<{
        eventAdditionalColumnValues: Record<string, unknown>;
        eventId: Uuid | null;
        templateId: Uuid;
    }>;
    organizationalUnitId?: Uuid | null;
    workspaceId: Uuid;
};

export type DuluxBlueStoreUpdateEventRequest = {
    eventAdditionalColumnValues: Record<string, unknown>;
    eventContainerAdditionalColumnValues: Record<string, unknown>;
    eventId: Uuid;
    organizationalUnitId?: Uuid | null;
    templateId: Uuid;
    workspaceId: Uuid;
};

type DuluxBlueStoreLeadProfileRequest = {
    contactNumber: string;
    customerAddress: string;
    customerCity: Uuid | null;
    customerName: string;
    customerPincode: Uuid | null;
    customerState: Uuid | null;
    eventContainerId?: Uuid | null;
    organizationalUnitId: Uuid;
    remarks?: string | null;
    workspaceId: Uuid;
};

export type DuluxBlueStoreScheduleActivityRequest = DuluxBlueStoreLeadProfileRequest & {
    scheduledForDate: Iso8601DateTime;
    scheduledForTimeSlot: string;
};

export type DuluxBlueStoreLogActivityRequest = DuluxBlueStoreLeadProfileRequest & {
    dispositionId: Uuid;
    eventId?: Uuid | null;
    loggedOn: Iso8601DateTime;
};

export type DuluxBlueStoreCancelActivityRequest = {
    cancellationReason: string;
    eventId: Uuid;
    remarks?: string | null;
    workspaceId: Uuid;
};

export type DuluxBlueStoreCloseRfqRequest = {
    eventId: Uuid;
    remarks?: string | null;
    workspaceId: Uuid;
};

async function postDuluxBlueStoreAction(args: {
    accessToken: string;
    body: Record<string, unknown>;
    path: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_BLUE_STORE_ACTIONS_API_BASE_PATH}${args.path}`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.workspaceId,
            }),
            body: JSON.stringify(args.body),
        },
        duluxBlueStoreActionResponseSchema,
    );
}

export function duluxBlueStoreScheduleCallAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreScheduleActivityRequest;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return postDuluxBlueStoreAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/schedule-call",
        body: args.request,
    });
}

export function duluxBlueStoreLogCallAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreLogActivityRequest;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return postDuluxBlueStoreAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/log-call",
        body: args.request,
    });
}

export function duluxBlueStoreCancelCallAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreCancelActivityRequest;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return postDuluxBlueStoreAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/cancel-call",
        body: args.request,
    });
}

export function duluxBlueStoreScheduleWalkinAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreScheduleActivityRequest;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return postDuluxBlueStoreAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/schedule-walkin",
        body: args.request,
    });
}

export function duluxBlueStoreLogWalkinAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreLogActivityRequest;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return postDuluxBlueStoreAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/log-walkin",
        body: args.request,
    });
}

export function duluxBlueStoreCancelWalkinAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreCancelActivityRequest;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return postDuluxBlueStoreAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/cancel-walkin",
        body: args.request,
    });
}

export function duluxBlueStoreCloseRfqAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreCloseRfqRequest;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return postDuluxBlueStoreAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/close-rfq",
        body: args.request,
    });
}

export function duluxBlueStoreUpdateEventContainerProfileAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreUpdateEventContainerProfileRequest;
}): Promise<Result<DuluxBlueStoreEventContainerProfileUpdateResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_BLUE_STORE_ACTIONS_API_BASE_PATH}/update-event-container-profile`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        duluxBlueStoreEventContainerProfileUpdateResponseSchema,
    );
}

export function duluxBlueStoreUpdateEventAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxBlueStoreUpdateEventRequest;
}): Promise<Result<DuluxBlueStoreActionResponse>> {
    return postDuluxBlueStoreAction({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.request.workspaceId,
        path: "/update-event",
        body: args.request,
    });
}

export function duluxDeleteEventContainerNoteAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxDeleteEventContainerNoteRequest;
}): Promise<Result<DuluxEventContainerNoteDeleteResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_ACTIONS_API_BASE_PATH}/event-container-notes/delete`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        duluxEventContainerNoteDeleteResponseSchema,
    );
}

export function duluxBlueStoreDeleteEventContainerNoteAction(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    request: DuluxDeleteEventContainerNoteRequest;
}): Promise<Result<DuluxEventContainerNoteDeleteResponse>> {
    return robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto${DULUX_BLUE_STORE_ACTIONS_API_BASE_PATH}/event-container-notes/delete`,
        {
            method: "POST",
            headers: buildDuluxActionHeaders({
                accessToken: args.accessToken,
                workspaceId: args.request.workspaceId,
            }),
            body: JSON.stringify(args.request),
        },
        duluxEventContainerNoteDeleteResponseSchema,
    );
}
